import { z } from "zod";
import { AddressComponents } from "../catalogItem";
import { DeadlineStep } from "../deadline";
import { MemberPreferredCommunicationChannel } from "../member.firestore";
import { MemberSatisfaction } from "../memberSatisfaction";
import { LatestMessageMeta } from "../message.firestore";
import { OfferingCategory } from "../offeringCategory";
import {
    AgentId,
    FirebaseId,
    LocalDate,
    LocalTime,
    MediumLongString,
    MemberId,
    Rating,
} from "../primitives";
import { FirebaseTimestamp } from "../record.firestore";
import { RequestBookingOutcome, RequestOutcome } from "./requestOutcome";
import { RequestSource } from "./requestSource";
import { RequestStatus } from "./requestStatus";

export const RequestAiCaptureStatus = z.enum(["analysing", "complete", "failed"]);
export type RequestAiCaptureStatus = z.infer<typeof RequestAiCaptureStatus>;

// TODO: Copied (partially)from firebase/lmt/src/common/types. Replace the file there with imports from this file and move tests if any.

export const RequestRequired = z.object({
    conversation: FirebaseId,
    createdBy: AgentId.optional().nullable(),
    member: MemberId,
    status: RequestStatus,
    createdFromMessage: FirebaseId.nullable().optional(),
    memberAssignedTo: AgentId.nullable().optional(),
});

export const RequestRequiredUpdatables = z.object({
    assignedTo: AgentId.nullable().optional(),
    assignedToName: z.string().nullable().optional(),
    title: z.string().optional().nullable(),
    status: RequestStatus.optional(),
    archived: z.boolean().optional(),
});

const ProposalMessageData = z.object({
    message: FirebaseId,
    bookings: z.array(FirebaseId),
    sentAt: FirebaseTimestamp,
});

const Proposal = z.object({
    messages: z.array(ProposalMessageData),
});

export type ProposalMessageData = z.infer<typeof ProposalMessageData>;

// // Think about body naming convention still
export const RequestMeta = z.object({
    createdAt: FirebaseTimestamp,
    updatedAt: FirebaseTimestamp.nullable().optional(),
    openedAt: FirebaseTimestamp.nullable().optional(),
    confirmedAt: FirebaseTimestamp.nullable().optional(),
    closedAt: FirebaseTimestamp.nullable().optional(),

    proposalBookings: z.array(FirebaseId).nullable().optional(),
    proposal: Proposal.nullable().optional(),
    ticketNumber: z.number(),
});

const RequestUpdatablesBase = z.object({
    title: z.string().optional().nullable(),
    offeringCategory: OfferingCategory.nullable().optional(),
    location: z.string().nullable().optional(), // Deprecated field from request but still used in the database
    firstChoice: z.string().nullable().optional(),
    memberSatisfaction: MemberSatisfaction.nullable().optional(),
    notes: z.string().nullable().optional(),
    numberOfGuests: z.number().nullable().optional(),
    outcome: RequestOutcome.nullable().optional(),
    bookingOutcome: RequestBookingOutcome.nullable().optional(),
    source: RequestSource.nullable().optional(),
    sourceChannel: MemberPreferredCommunicationChannel.nullable().optional(),
    budget: z.string().nullable().optional(),

    startDate: LocalDate.nullable().optional(),
    startTime: LocalTime.nullable().optional(),
    endDate: LocalDate.nullable().optional(),
    endTime: LocalTime.nullable().optional(),

    nextDeadlineStep: DeadlineStep.nullable().optional(),
    nextDeadlineAt: FirebaseTimestamp.nullable().optional(),
    deadlineCreatedAt: FirebaseTimestamp.nullable().optional(),

    latestMessage: LatestMessageMeta.nullable().optional(),
});
type RequestUpdatablesBase = z.infer<typeof RequestUpdatablesBase>;

export const RequestHighlightedFields = z.array(
    RequestUpdatablesBase.pick({
        offeringCategory: true,
        location: true,
        numberOfGuests: true,
        startDate: true,
        startTime: true,
        endDate: true,
        endTime: true,
        budget: true,
        firstChoice: true,
    })
        .extend(RequestRequiredUpdatables.pick({ title: true }).shape)
        // Marker for the Google-place-backed "City or area" sidebar field.
        .extend({ locationGooglePlaceId: z.string().nullable().optional() })
        .keyof(),
);
export type RequestHighlightedFields = z.infer<typeof RequestHighlightedFields>;

export const RequestGooglePlaceLocation = z.object({
    locationGooglePlaceId: z.string().optional().nullable(),
    locationGoogleName: z.string().optional().nullable(),
    locationGoogleFormattedAddress: z.string().optional().nullable(),
    locationGoogleAddressComponents: AddressComponents.optional().nullable(),
    locationGoogleGeometryLat: z.number().optional().nullable(),
    locationGoogleGeometryLng: z.number().optional().nullable(),
});
export type RequestGooglePlaceLocation = z.infer<typeof RequestGooglePlaceLocation>;

export const RequestUpdatables = RequestUpdatablesBase.extend(
    RequestGooglePlaceLocation.shape,
).extend(
    z.object({
        highlightedFields: RequestHighlightedFields.optional().nullable(),
        isAssistedByAI: z.boolean().optional().nullable(),
        isCreatedByAI: z.boolean().optional().nullable(),
        aiCaptureStatus: RequestAiCaptureStatus.optional().nullable(),
    }).shape,
);

export const RequestFeedback = z.object({
    memberRating: Rating,
    memberFeedback: MediumLongString.optional().nullable(),
    memberFeedbackTimestamp: FirebaseTimestamp.optional().nullable(),
});
export type RequestFeedback = z.infer<typeof RequestFeedback>;

export type RequestRequired = z.infer<typeof RequestRequired>;
export type RequestRequiredUpdatables = z.infer<typeof RequestRequiredUpdatables>;
export type RequestMeta = z.infer<typeof RequestMeta>;
export type RequestUpdatables = z.infer<typeof RequestUpdatables>;

export const RequestDoc = RequestRequiredUpdatables.extend(RequestRequired.shape)
    .extend(RequestMeta.shape)
    .extend(RequestUpdatables.shape)
    .extend(RequestFeedback.shape);
export type RequestDoc = z.infer<typeof RequestDoc>;

const RequestRequireArchived = z.object({ archived: z.literal(false) });

export const RequestPayload = RequestRequired.extend(
    RequestRequiredUpdatables.shape,
).extend(RequestRequireArchived.shape);
export type RequestPayload = z.infer<typeof RequestPayload>;

export const RequestPayloadWithUpdatables = RequestRequiredUpdatables.extend(
    RequestRequired.shape,
)
    .extend(RequestUpdatables.shape)
    .extend(RequestRequireArchived.shape);
export type RequestPayloadWithUpdatables = z.infer<typeof RequestPayloadWithUpdatables>;

export const RequestAgentUpdatables = RequestRequiredUpdatables.extend(
    RequestUpdatables.shape,
);
export type RequestAgentUpdatables = z.infer<typeof RequestAgentUpdatables>;

export const Request = z.object({ id: FirebaseId }).extend(RequestDoc.shape);
export type Request = z.infer<typeof Request>;

export const MemberRequestDoc = RequestDoc.pick({
    assignedTo: true,
    assignedToName: true,
    title: true,
    offeringCategory: true,
    createdAt: true,
    openedAt: true,
    location: true,
    startDate: true,
    startTime: true,
    endDate: true,
    endTime: true,
    updatedAt: true,
    nextDeadlineAt: true,
    nextDeadlineStep: true,
    deadlineCreatedAt: true,
    numberOfGuests: true,
    status: true,
    createdFromMessage: true,
    closedAt: true,
    memberFeedbackTimestamp: true,
    memberRating: true,
    memberFeedback: true,
    proposalBookings: true,
    proposal: true,
    latestMessage: true,
});
export type MemberRequestDoc = z.infer<typeof MemberRequestDoc>;

export const MemberRequest = z
    .object({ id: FirebaseId })
    .extend(MemberRequestDoc.shape);
export type MemberRequest = z.infer<typeof MemberRequest>;
